import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Star, MapPin, DollarSign, Calendar, MessageCircle, Facebook, Instagram, Twitter, Linkedin, Youtube, Globe, Eye, Crown, Rocket, Award, Zap, Trophy, Medal, Gem, Diamond, Sparkles, Flame, Shield, Target, TrendingUp, ArrowUpCircle, BadgeCheck, Heart, ThumbsUp, Users, UserCheck, CheckCircle, Sun, Moon, Clock, Music2, Video } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { mockTrainers, mockReviews } from '../mockData';
import BookingModal from '../components/BookingModal';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from '../hooks/use-toast';
import { getTimezoneDisplayName } from '../data/canadaCities';
import { getUTCOffset, getUserEffectiveTimezone, formatGroupClassDateTime } from '../utils/timezoneUtils';

// Icon mapping for achievement badges
const BadgeIconMap = {
  Crown, Star, Rocket, Award, Zap, Trophy, Medal,
  Gem, Diamond, Sparkles, Fire: Flame, Bolt: Zap, Shield, Target,
  TrendingUp, ArrowUpCircle, BadgeCheck, Heart, ThumbsUp,
  Eye, Users, UserCheck, Verified: BadgeCheck, CheckCircle
};

const TrainerProfilePage = () => {
  const { id } = useParams();
  const { user, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [trainer, setTrainer] = useState(null);
  const [trainerReviews, setTrainerReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [hasBooking, setHasBooking] = useState(false);
  const [checkingBooking, setCheckingBooking] = useState(false);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [sportCategories, setSportCategories] = useState([]);
  const [groupClasses, setGroupClasses] = useState([]);
  
  // Lightbox states
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');
  const [lightboxTitle, setLightboxTitle] = useState('');

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  
  // Format group class datetime using user's profile timezone (or browser fallback)
  const formatClassDateTime = (scheduledDate, startTime, trainerTimezone, utcTime) => {
    return formatGroupClassDateTime(scheduledDate, startTime, trainerTimezone, utcTime, user);
  };
  
  // Helper function to ensure URL has proper protocol
  const normalizeUrl = (url) => {
    if (!url) return url;
    // If URL already has a protocol, return as-is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Otherwise, add https://
    return `https://${url}`;
  };
  
  // Helper function to get emoji for a sport category
  const getSportEmoji = (sportName) => {
    const category = sportCategories.find(cat => 
      cat.name.toLowerCase() === sportName.toLowerCase()
    );
    return category?.icon || category?.emoji || '💪';
  };
  
  // Lightbox handlers
  const openLightbox = (imageUrl, title = '') => {
    setLightboxImage(imageUrl);
    setLightboxTitle(title);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden'; // Prevent body scroll
  };
  
  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage('');
    setLightboxTitle('');
    document.body.style.overflow = 'auto'; // Restore body scroll
  };
  
  // Close lightbox on ESC key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && lightboxOpen) {
        closeLightbox();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [lightboxOpen]);
  
  // Check if logged-in user is viewing their own profile
  useEffect(() => {
    const checkOwnProfile = () => {
      // Get user from localStorage (more reliable than context)
      const storedUser = localStorage.getItem('user');
      
      if (!storedUser || !trainer) {
        setIsOwnProfile(false);
        return;
      }
      
      try {
        const currentUser = JSON.parse(storedUser);
        
        // Simple email check - most reliable
        if (currentUser.email && trainer.email) {
          const isOwn = currentUser.email.toLowerCase() === trainer.email.toLowerCase();
          setIsOwnProfile(isOwn);
          
          if (isOwn) {
            console.log('🔒 This is your own profile - buttons hidden');
          }
        } else {
          setIsOwnProfile(false);
        }
        
      } catch (error) {
        setIsOwnProfile(false);
      }
    };
    
    checkOwnProfile();
  }, [trainer]);

  // All hooks must be called before any conditional returns
  const nextImage = () => {
    if (trainer && trainer.photos) {
      setCurrentImageIndex((prev) => (prev + 1) % trainer.photos.length);
    }
  };

  const prevImage = () => {
    if (trainer && trainer.photos) {
      setCurrentImageIndex((prev) => (prev - 1 + trainer.photos.length) % trainer.photos.length);
    }
  };

  const handleBooking = () => {
    if (!isAuthenticated) {
      toast({
        title: 'Login Required',
        description: 'Please login to book a session',
        variant: 'destructive',
      });
      return;
    }
    setShowBookingModal(true);
  };

  const handleMessage = async () => {
    if (!isAuthenticated) {
      toast({
        title: 'Login Required',
        description: 'Please login to send messages',
        variant: 'destructive',
      });
      return;
    }

    if (!hasBooking) {
      toast({
        title: 'Booking Required',
        description: 'You need to book a session with this trainer before you can message them',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      // Create or get conversation
      const response = await fetch(`${API_BASE}/api/messages/conversations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          participant_id: trainer.user_id
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast({
          title: 'Success',
          description: 'Redirecting to messages...',
        });
        navigate('/user/messages');
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to start conversation',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to start conversation',
        variant: 'destructive',
      });
    }
  };

  // Fetch trainer data from backend
  useEffect(() => {
    const fetchTrainer = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE}/api/trainers/${id}`);
        
        if (response.ok) {
          const data = await response.json();
          
          // Fetch social media links
          try {
            const socialMediaResponse = await fetch(`${API_BASE}/api/trainers/${id}/social-media`);
            if (socialMediaResponse.ok) {
              const socialMediaData = await socialMediaResponse.json();
              data.social_media = socialMediaData.social_media || {};
            }
          } catch (error) {
            console.error('Error fetching social media:', error);
            data.social_media = {};
          }
          
          // Fetch sport categories for emojis
          try {
            const categoriesResponse = await fetch(`${API_BASE}/api/proficiency-icons`);
            if (categoriesResponse.ok) {
              const icons = await categoriesResponse.json();
              // Map to format: { name, icon }
              const categories = icons.map((icon, index) => ({
                id: icon.id || index,
                name: icon.name,
                icon: icon.icon_url,
                order: icon.display_order || 0
              }));
              setSportCategories(categories);
            }
          } catch (error) {
            console.error('Error fetching sport categories:', error);
          }
          
          setTrainer(data);
          
          // Fetch reviews for this trainer
          try {
            const reviewsResponse = await fetch(`${API_BASE}/api/reviews/trainer/${id}`);
            if (reviewsResponse.ok) {
              const reviewsData = await reviewsResponse.json();
              setTrainerReviews(reviewsData);
            }
          } catch (error) {
            console.error('Error fetching reviews:', error);
          }
          
          // Fetch public group classes for this trainer
          try {
            // Use trainer slug for group classes API
            const trainerSlug = data.slug || id;
            const classesResponse = await fetch(`${API_BASE}/api/group-classes/trainer/${trainerSlug}`);
            if (classesResponse.ok) {
              const classesData = await classesResponse.json();
              setGroupClasses(classesData.classes || []);
            }
          } catch (error) {
            console.error('Error fetching group classes:', error);
          }
          
          // Track profile view after trainer data is loaded
          const token = localStorage.getItem('token');
          console.log('[TRACK-VIEW] Check - token:', !!token, 'trainerId:', data.id);
          if (token) {
            // Only track for logged-in users
            const trackKey = `profile_view_${id}`;
            if (!sessionStorage.getItem(trackKey)) {
              try {
                console.log('[TRACK-VIEW] Tracking view for trainer:', id);
                const response = await fetch(`${API_BASE}/api/trainers/${id}/track-view`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  }
                });
                const result = await response.json();
                console.log('[TRACK-VIEW] Result:', result);
                sessionStorage.setItem(trackKey, 'true');
              } catch (error) {
                console.debug('[TRACK-VIEW] Failed:', error);
              }
            } else {
              console.log('[TRACK-VIEW] Skip - already tracked this session');
            }
          } else {
            console.log('[TRACK-VIEW] Skip - no token');
          }
        } else {
          setTrainer(null);
        }
      } catch (error) {
        console.error('Error fetching trainer:', error);
        setTrainer(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchTrainer();
    }
  }, [id, API_BASE, user]);

  // Check if user has booking with this trainer
  useEffect(() => {
    const checkBooking = async () => {
      if (!isAuthenticated || !user || user.role !== 'user' || !trainer) return;
      
      setCheckingBooking(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE}/api/bookings/user`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.ok) {
          const bookings = await response.json();
          // Check if user has any booking with this trainer
          const hasTrainerBooking = bookings.some(
            booking => booking.trainer_id === trainer?.id || booking.trainer_name === trainer?.name
          );
          setHasBooking(hasTrainerBooking);
        }
      } catch (error) {
        console.error('Error checking bookings:', error);
      } finally {
        setCheckingBooking(false);
      }
    };
    
    checkBooking();
  }, [isAuthenticated, user, trainer?.id, trainer?.name, API_BASE]);

  // Early returns AFTER all hooks
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400 text-lg">Loading trainer profile...</p>
        </div>
      </div>
    );
  }

  if (!trainer) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Trainer not found</h2>
          <Link to="/">
            <Button>Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src={theme === 'dark' ? '/trainerlist-logo-dark.png' : '/trainerlist-logo.png'} 
              alt="TrainerList" 
              className="h-10 sm:h-12 md:h-14 w-auto object-contain"
              onError={(e) => {
                e.target.src = '/trainerlist-logo.png';
              }}
            />
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5 text-gray-700" />}
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <Link to="/" className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mb-6">
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Trainers
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Images and Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Trainer Photos */}
            <Card className="dark:bg-gray-800 dark:border-gray-700">
              <CardHeader>
                <CardTitle className="dark:text-gray-100">Trainer Photos</CardTitle>
              </CardHeader>
              <CardContent>
                {trainer.photos && trainer.photos.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {trainer.photos.map((photo, index) => {
                      // Check if photo is a full URL or a relative path
                      const photoUrl = photo.startsWith('http') ? photo : `${API_BASE}${photo}`;
                      return (
                        <div key={photoUrl} className="border dark:border-gray-700 rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer bg-gray-100 dark:bg-gray-700">
                          <div 
                            onClick={() => openLightbox(photoUrl, `Trainer Photo ${index + 1}`)}
                            className="block"
                          >
                            <img 
                              src={photoUrl} 
                              alt={`Trainer photo ${index + 1}`}
                              className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = theme === 'dark' ? '/trainer-placeholder-logo-dark-t.png' : '/trainer-placeholder-logo-t.png';
                                e.target.className = 'w-full h-64 object-contain p-4';
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-64 bg-white dark:bg-gray-800 rounded-lg overflow-hidden">
                    <img 
                      src={theme === 'dark' ? '/trainer-placeholder-logo-dark-t.png' : '/trainer-placeholder-logo-t.png'}
                      alt="No photos available"
                      className="w-full h-full object-contain p-6"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Trainer Info Card - Mobile Only (shown right after photos on mobile) */}
            <Card className="lg:hidden dark:bg-gray-800 dark:border-gray-700">
              <CardContent className="p-6">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold mb-2 dark:text-gray-100">{trainer.name}</h2>
                  {trainer.is_sponsored && (
                    <Badge className="mb-2">Sponsored Trainer</Badge>
                  )}
                  
                  {/* Achievement Badges - Mobile */}
                  {trainer.achievement_badges && trainer.achievement_badges.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {trainer.achievement_badges.map((badge) => {
                        const BadgeIcon = BadgeIconMap[badge.icon] || Trophy;
                        return (
                          <Badge 
                            key={badge.key}
                            className="text-white text-xs px-2 py-1 flex items-center gap-1"
                            style={{ backgroundColor: badge.color }}
                            title={badge.name}
                          >
                            <BadgeIcon className="w-3 h-3" />
                            <span>{badge.badge_text}</span>
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-4 mb-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      <span className="font-semibold dark:text-gray-200">{trainer.rating || 0}</span>
                      <span className="text-gray-500 dark:text-gray-400">({trainer.review_count || 0} reviews)</span>
                    </div>
                    {trainer.profile_views !== undefined && (
                      <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/30 px-3 py-1 rounded-full">
                        <Eye className="w-4 h-4 text-red-600 dark:text-red-400" />
                        <span className="text-sm font-semibold text-red-700 dark:text-red-300">{trainer.profile_views} views</span>
                      </div>
                    )}
                  </div>
                </div>

                <Separator className="my-4 dark:bg-gray-700" />

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                    <MapPin className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="block">
                        {trainer.location?.formatted_address || (
                          <>
                            {trainer.location?.street && <span>{trainer.location.street}, </span>}
                            {trainer.street && !trainer.location?.street && <span>{trainer.street}, </span>}
                            {trainer.location?.city}, {trainer.location?.province}
                          </>
                        )}
                      </span>
                      {trainer.location?.country && trainer.location.country !== 'Canada' && trainer.location.country !== 'Kanada' && (
                        <span className="block text-xs text-gray-400 dark:text-gray-500">
                          {trainer.location.country}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Trainer Timezone Display */}
                  {trainer.timezone && (
                    <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                      <Clock className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                      <div className="flex items-center gap-2">
                        <span>{getTimezoneDisplayName(trainer.timezone)}</span>
                        <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
                          {getUTCOffset(trainer.timezone)}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                    <DollarSign className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                    <span>${trainer.pricePerHour || trainer.price_per_hour}/hour</span>
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="font-semibold mb-3 text-lg dark:text-gray-200">Specializations</h4>
                  <div className="flex flex-wrap gap-2">
                    {trainer.specializations.map((spec) => (
                      <Badge 
                        key={spec} 
                        variant="secondary" 
                        className="dark:bg-gray-700 dark:text-gray-300 text-base py-1.5 px-3"
                      >
                        <span className="mr-1.5">{getSportEmoji(spec)}</span>
                        {spec}
                      </Badge>
                    ))}
                  </div>
                </div>

                {trainer.training_types && trainer.training_types.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2 dark:text-gray-200">Training Type</h4>
                    <div className="flex flex-wrap gap-2">
                      {trainer.training_types.map((type) => (
                        <Badge 
                          key={type} 
                          className="bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50"
                        >
                          {type === 'Online' && '🌐 '}
                          {type === 'In Person' && '🏋️ '}
                          {type === 'Both' && '🌐🏋️ '}
                          {type}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {trainer.gender && (
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2 dark:text-gray-200">Gender</h4>
                    <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
                      {trainer.gender === 'male' && '♂️ '}
                      {trainer.gender === 'female' && '♀️ '}
                      {trainer.gender === 'other' && '⚧️ '}
                      <span className="capitalize">{trainer.gender}</span>
                    </Badge>
                  </div>
                )}

                {/* Availability Section */}
                {trainer.availability?.summary && trainer.availability.summary.days_display !== 'Not set' && (
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2 dark:text-gray-200">Availability</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50">
                          🗓️ {trainer.availability.summary.days_display}
                        </Badge>
                      </div>
                      {trainer.availability.weekly_schedule && (
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(trainer.availability.weekly_schedule)
                            .filter(([_, data]) => data.enabled && data.slots?.length > 0)
                            .map(([dayKey, data]) => {
                              // Calculate time ranges for this day
                              const sortedSlots = [...data.slots].sort();
                              const ranges = [];
                              let rangeStart = sortedSlots[0];
                              let prevHour = parseInt(sortedSlots[0].split(':')[0]);
                              
                              for (let i = 1; i <= sortedSlots.length; i++) {
                                const currentSlot = sortedSlots[i];
                                const currentHour = currentSlot ? parseInt(currentSlot.split(':')[0]) : -1;
                                
                                if (currentHour !== prevHour + 1) {
                                  const endHour = prevHour + 1;
                                  ranges.push(`${rangeStart}-${endHour.toString().padStart(2, '0')}:00`);
                                  if (currentSlot) rangeStart = currentSlot;
                                }
                                prevHour = currentHour;
                              }
                              
                              const dayNames = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
                              
                              return (
                                <div 
                                  key={dayKey}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-900/20 rounded text-xs"
                                >
                                  <span className="font-semibold text-green-700 dark:text-green-400">{dayNames[dayKey]}:</span>
                                  <span className="text-green-600 dark:text-green-300">{ranges.join(', ')}</span>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {trainer.age && (
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2 dark:text-gray-200">Age</h4>
                    <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50">
                      🎂 {trainer.age} years old
                    </Badge>
                  </div>
                )}

                <Separator className="my-4 dark:bg-gray-700" />

                <div className="space-y-3">
                  {isAuthenticated ? (
                    <>
                      {isOwnProfile ? (
                        <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg text-center">
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            This is your profile
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            You cannot book a session or message yourself
                          </p>
                        </div>
                      ) : (
                        <>
                          {trainer.is_bookable === false ? (
                            <div className="space-y-2">
                              <Button 
                                disabled
                                className="w-full bg-gray-400 cursor-not-allowed"
                              >
                                <Calendar className="w-4 h-4 mr-2" />
                                Booking Unavailable
                              </Button>
                              <p className="text-xs text-amber-600 dark:text-amber-400 text-center bg-amber-50 dark:bg-amber-900/20 p-2 rounded-lg">
                                ⚠️ {trainer.booking_disabled_reason || 'This trainer is not currently accepting new bookings.'}
                              </p>
                            </div>
                          ) : (
                            <Button 
                              onClick={handleBooking} 
                              className="w-full bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900"
                            >
                              <Calendar className="w-4 h-4 mr-2" />
                              Book a Session
                            </Button>
                          )}
                          <Button 
                            onClick={handleMessage} 
                            variant="outline" 
                            className="w-full"
                            disabled={checkingBooking || (!hasBooking && user?.role === 'user')}
                            title={!hasBooking && user?.role === 'user' ? 'Book a session first to message this trainer' : ''}
                          >
                            <MessageCircle className="w-4 h-4 mr-2" />
                            {checkingBooking ? 'Checking...' : 'Send Message'}
                          </Button>
                          {!hasBooking && user?.role === 'user' && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                              Book a session to message this trainer
                            </p>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <Button 
                      onClick={() => navigate('/login')} 
                      className="w-full bg-gradient-to-r from-red-600 to-black hover:from-red-700 hover:to-gray-900"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Login to Book
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Online Group Classes */}
            {groupClasses.length > 0 && (
              <Card className="dark:bg-gray-800 dark:border-gray-700 border-2 border-green-500/30 bg-green-50/30 dark:bg-green-900/10">
                <CardHeader className="pb-3">
                  <CardTitle className="dark:text-gray-100 flex items-center gap-2 text-green-700 dark:text-green-400">
                    <Video className="w-5 h-5" />
                    Online Group Classes
                    {groupClasses.some(gc => gc.status === 'live') && (
                      <Badge className="bg-red-600 text-white ml-2 animate-pulse">🔴 LIVE</Badge>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {groupClasses.slice(0, 3).map((groupClass) => (
                    <div
                      key={groupClass.id}
                      className={`p-4 bg-white dark:bg-gray-700 rounded-lg border ${
                        groupClass.status === 'live' 
                          ? 'border-red-400 dark:border-red-500 ring-2 ring-red-200 dark:ring-red-800' 
                          : 'border-gray-200 dark:border-gray-600'
                      } hover:shadow-md transition-shadow cursor-pointer`}
                      onClick={() => navigate(`/group-class/${groupClass.slug}`)}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-gray-900 dark:text-white truncate">
                              {groupClass.title}
                            </h4>
                            {/* Dynamic status badge */}
                            {groupClass.status === 'live' ? (
                              <Badge className="bg-red-600 text-white text-xs animate-pulse">LIVE</Badge>
                            ) : groupClass.status === 'upcoming' ? (
                              <Badge className="bg-blue-600 text-white text-xs">Upcoming</Badge>
                            ) : null}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                            {groupClass.short_description}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatClassDateTime(groupClass.scheduled_date, groupClass.start_time, groupClass.trainer_timezone, groupClass.start_time_utc)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {groupClass.current_participants}/{groupClass.max_participants}
                            </span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          className={`shrink-0 ${
                            groupClass.status === 'live' 
                              ? 'bg-red-600 hover:bg-red-700 animate-pulse' 
                              : 'bg-green-600 hover:bg-green-700'
                          } text-white`}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/group-class/${groupClass.slug}`);
                          }}
                        >
                          {groupClass.status === 'live' ? 'Join Now' : 'Join'}
                        </Button>
                      </div>
                    </div>
                  ))}
                  {groupClasses.length > 3 && (
                    <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                      + {groupClasses.length - 3} more classes available
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* About */}
            <Card className="dark:bg-gray-800 dark:border-gray-700">
              <CardHeader>
                <CardTitle className="dark:text-gray-100">About {trainer.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line break-words" data-testid="trainer-bio-text">{(trainer.bio || "").replace(/\r\n?/g, "\n").replace(/\n{3,}/g, "\n\n")}</p>
              </CardContent>
            </Card>

            {/* Social Media Links */}
            {trainer.social_media && Object.values(trainer.social_media).some(link => link) && (
              <Card className="dark:bg-gray-800 dark:border-gray-700">
                <CardHeader>
                  <CardTitle className="dark:text-gray-100">Connect with {trainer.name.split(' ')[0]}</CardTitle>
                </CardHeader>
                <CardContent>
                  {isAuthenticated ? (
                    <div className="flex flex-wrap gap-3">
                      {trainer.social_media.facebook && (
                        <a
                          href={normalizeUrl(trainer.social_media.facebook)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-full hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                        >
                          <Facebook className="w-5 h-5" />
                          <span className="text-sm font-medium">Facebook</span>
                        </a>
                      )}
                      {trainer.social_media.instagram && (
                        <a
                          href={normalizeUrl(trainer.social_media.instagram)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-full hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                        >
                          <Instagram className="w-5 h-5" />
                          <span className="text-sm font-medium">Instagram</span>
                        </a>
                      )}
                      {trainer.social_media.twitter && (
                        <a
                          href={normalizeUrl(trainer.social_media.twitter)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-400 dark:text-red-300 rounded-full hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                        >
                          <Twitter className="w-5 h-5" />
                          <span className="text-sm font-medium">Twitter</span>
                        </a>
                      )}
                      {trainer.social_media.linkedin && (
                        <a
                          href={normalizeUrl(trainer.social_media.linkedin)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-full hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                        >
                          <Linkedin className="w-5 h-5" />
                          <span className="text-sm font-medium">LinkedIn</span>
                        </a>
                      )}
                      {trainer.social_media.youtube && (
                        <a
                          href={normalizeUrl(trainer.social_media.youtube)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-full hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                        >
                          <Youtube className="w-5 h-5" />
                          <span className="text-sm font-medium">YouTube</span>
                        </a>
                      )}
                      {trainer.social_media.whatsapp && trainer.whatsappVisibility !== 'hidden' && (
                        <a
                          href={`https://wa.me/${trainer.social_media.whatsapp.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                        >
                          <MessageCircle className="w-5 h-5" />
                          <span className="text-sm font-medium">WhatsApp</span>
                        </a>
                      )}
                      {trainer.social_media.tiktok && (
                        <a
                          href={normalizeUrl(trainer.social_media.tiktok)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-gray-700 text-white rounded-full hover:bg-gray-800 dark:hover:bg-gray-600 transition-colors"
                        >
                          <Music2 className="w-5 h-5" />
                          <span className="text-sm font-medium">TikTok</span>
                        </a>
                      )}
                      {trainer.social_media.website && (
                        <a
                          href={normalizeUrl(trainer.social_media.website)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                          <Globe className="w-5 h-5" />
                          <span className="text-sm font-medium">Website</span>
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-gray-500 dark:text-gray-400 mb-3">Login to view social media links</p>
                      <Button 
                        onClick={() => navigate('/login')}
                        className="bg-red-600 hover:bg-red-700 text-white"
                      >
                        Login to Connect
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Certificates */}
            {trainer.certificates && trainer.certificates.length > 0 && (
              <Card className="dark:bg-gray-800 dark:border-gray-700">
                <CardHeader>
                  <CardTitle className="dark:text-gray-100">Certifications & Credentials</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {trainer.certificates.map((cert, index) => {
                      const certUrl = `${API_BASE}${cert.url || cert}`;
                      const certName = cert.filename || `Certificate ${index + 1}`;
                      return (
                        <div key={certUrl} className="border dark:border-gray-700 rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer">
                          <div 
                            onClick={() => openLightbox(certUrl, certName)}
                            className="block"
                          >
                            <img 
                              src={certUrl} 
                              alt={certName}
                              className="w-full h-48 object-cover bg-gray-100 dark:bg-gray-700 hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="18" dy="150" dx="100"%3ECertificate%3C/text%3E%3C/svg%3E';
                              }}
                            />
                            <div className="p-3 bg-gray-50 dark:bg-gray-800">
                              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {certName}
                              </p>
                              {cert.uploaded_at && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  Uploaded: {new Date(cert.uploaded_at).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Reviews */}
            <Card className="dark:bg-gray-800 dark:border-gray-700">
              <CardHeader>
                <CardTitle className="dark:text-gray-100">Reviews ({trainerReviews.length})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {trainerReviews.map((review) => (
                  <div key={review.id} className="border-b dark:border-gray-700 last:border-b-0 pb-4 last:pb-0">
                    <div className="flex items-start gap-3">
                      <Avatar>
                        <AvatarFallback className="dark:bg-gray-700 dark:text-gray-300">{(review.user_name || review.userName || 'U').charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-semibold dark:text-gray-200">{review.user_name || review.userName || 'Anonymous'}</h4>
                          <span className="text-sm text-gray-500 dark:text-gray-400">{review.created_at || review.date}</span>
                        </div>
                        <div className="flex items-center gap-1 mb-2">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={`star-${i}`}
                              className={`w-4 h-4 ${
                                i < (review.rating || 0) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300 dark:text-gray-600'
                              }`}
                            />
                          ))}
                        </div>
                        <p className="text-gray-700 dark:text-gray-300">{review.comment}</p>
                      </div>
                    </div>
                  </div>
                ))}

                {trainerReviews.length === 0 && (
                  <p className="text-gray-500 dark:text-gray-400 text-center py-4">No reviews yet</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Booking Card (Desktop Only) */}
          <div className="hidden lg:block lg:col-span-1">
            <Card className="sticky top-4 dark:bg-gray-800 dark:border-gray-700">
              <CardContent className="p-6">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold mb-2 dark:text-gray-100">{trainer.name}</h2>
                  {trainer.is_sponsored && (
                    <Badge className="mb-2">Sponsored Trainer</Badge>
                  )}
                  
                  {/* Achievement Badges - Desktop */}
                  {trainer.achievement_badges && trainer.achievement_badges.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {trainer.achievement_badges.map((badge) => {
                        const BadgeIcon = BadgeIconMap[badge.icon] || Trophy;
                        return (
                          <Badge 
                            key={badge.key}
                            className="text-white text-xs px-2 py-1 flex items-center gap-1"
                            style={{ backgroundColor: badge.color }}
                            title={badge.name}
                          >
                            <BadgeIcon className="w-3 h-3" />
                            <span>{badge.badge_text}</span>
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-4 mb-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      <span className="font-semibold dark:text-gray-200">{trainer.rating || 0}</span>
                      <span className="text-gray-500 dark:text-gray-400">({trainer.review_count || 0} reviews)</span>
                    </div>
                    {trainer.profile_views !== undefined && (
                      <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/30 px-3 py-1 rounded-full">
                        <Eye className="w-4 h-4 text-red-600 dark:text-red-400" />
                        <span className="text-sm font-semibold text-red-700 dark:text-red-300">{trainer.profile_views} views</span>
                      </div>
                    )}
                  </div>
                </div>

                <Separator className="my-4 dark:bg-gray-700" />

                <div className="space-y-3 mb-6">
                  <div className="flex items-start gap-3 text-gray-700 dark:text-gray-300">
                    <MapPin className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="block">
                        {trainer.location?.formatted_address || (
                          <>
                            {trainer.location?.street && <span>{trainer.location.street}, </span>}
                            {trainer.street && !trainer.location?.street && <span>{trainer.street}, </span>}
                            {trainer.location?.city}, {trainer.location?.province}
                          </>
                        )}
                      </span>
                      {trainer.location?.country && trainer.location.country !== 'Canada' && trainer.location.country !== 'Kanada' && (
                        <span className="block text-xs text-gray-400 dark:text-gray-500">
                          {trainer.location.country}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Trainer Timezone Display */}
                  {trainer.timezone && (
                    <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                      <Clock className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                      <div className="flex items-center gap-2">
                        <span>{getTimezoneDisplayName(trainer.timezone)}</span>
                        <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
                          {getUTCOffset(trainer.timezone)}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                    <DollarSign className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                    <span>${trainer.pricePerHour || trainer.price_per_hour}/hour</span>
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="font-semibold mb-3 text-lg dark:text-gray-200">Specializations</h4>
                  <div className="flex flex-wrap gap-2">
                    {trainer.specializations.map((spec) => (
                      <Badge 
                        key={spec} 
                        variant="secondary" 
                        className="dark:bg-gray-700 dark:text-gray-300 text-base py-1.5 px-3"
                      >
                        <span className="mr-1.5">{getSportEmoji(spec)}</span>
                        {spec}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Training Types */}
                {trainer.training_types && trainer.training_types.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2 dark:text-gray-200">Training Type</h4>
                    <div className="flex flex-wrap gap-2">
                      {trainer.training_types.map((type) => (
                        <Badge 
                          key={type} 
                          className="bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50"
                        >
                          {type === 'Online' && '🌐 '}
                          {type === 'In Person' && '🏋️ '}
                          {type === 'Both' && '🌐🏋️ '}
                          {type}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Gender */}
                {trainer.gender && (
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2 dark:text-gray-200">Gender</h4>
                    <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
                      {trainer.gender === 'male' && '♂️ '}
                      {trainer.gender === 'female' && '♀️ '}
                      {trainer.gender === 'other' && '⚧️ '}
                      <span className="capitalize">{trainer.gender}</span>
                    </Badge>
                  </div>
                )}

                {/* Availability Section */}
                {trainer.availability?.summary && trainer.availability.summary.days_display !== 'Not set' && (
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2 dark:text-gray-200">Availability</h4>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50">
                          🗓️ {trainer.availability.summary.days_display}
                        </Badge>
                      </div>
                      {trainer.availability.weekly_schedule && (
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(trainer.availability.weekly_schedule)
                            .filter(([_, data]) => data.enabled && data.slots?.length > 0)
                            .map(([dayKey, data]) => {
                              // Calculate time ranges for this day
                              const sortedSlots = [...data.slots].sort();
                              const ranges = [];
                              let rangeStart = sortedSlots[0];
                              let prevHour = parseInt(sortedSlots[0].split(':')[0]);
                              
                              for (let i = 1; i <= sortedSlots.length; i++) {
                                const currentSlot = sortedSlots[i];
                                const currentHour = currentSlot ? parseInt(currentSlot.split(':')[0]) : -1;
                                
                                if (currentHour !== prevHour + 1) {
                                  const endHour = prevHour + 1;
                                  ranges.push(`${rangeStart}-${endHour.toString().padStart(2, '0')}:00`);
                                  if (currentSlot) rangeStart = currentSlot;
                                }
                                prevHour = currentHour;
                              }
                              
                              const dayNames = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
                              
                              return (
                                <div 
                                  key={dayKey}
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-900/20 rounded text-xs"
                                >
                                  <span className="font-semibold text-green-700 dark:text-green-400">{dayNames[dayKey]}:</span>
                                  <span className="text-green-600 dark:text-green-300">{ranges.join(', ')}</span>
                                </div>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {trainer.age && (
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2 dark:text-gray-200">Age</h4>
                    <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50">
                      🎂 {trainer.age} years old
                    </Badge>
                  </div>
                )}

                <Separator className="my-4 dark:bg-gray-700" />

                <div className="space-y-3">
                  {isOwnProfile ? (
                    <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg text-center">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        This is your profile
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        You cannot book a session or message yourself
                      </p>
                    </div>
                  ) : (
                    <>
                      {trainer.is_bookable === false ? (
                        <div className="space-y-2">
                          <Button
                            disabled
                            className="w-full bg-gray-400 cursor-not-allowed"
                            size="lg"
                          >
                            <Calendar className="w-4 h-4 mr-2" />
                            Booking Unavailable
                          </Button>
                          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                            <p className="text-sm text-amber-700 dark:text-amber-300 text-center">
                              ⚠️ {trainer.booking_disabled_reason || 'This trainer is not currently accepting new bookings.'}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <Button
                          onClick={handleBooking}
                          className="w-full bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
                          size="lg"
                        >
                          <Calendar className="w-4 h-4 mr-2" />
                          Book a Session
                        </Button>
                      )}
                      <Button
                        onClick={handleMessage}
                        variant="outline"
                        className="w-full dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        size="lg"
                        disabled={checkingBooking || (!hasBooking && isAuthenticated && user?.role === 'user')}
                        title={!hasBooking && isAuthenticated && user?.role === 'user' ? 'Book a session first to message this trainer' : ''}
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        {checkingBooking ? 'Checking...' : 'Send Message'}
                      </Button>
                      {!hasBooking && isAuthenticated && user?.role === 'user' && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                          Book a session to message this trainer
                        </p>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {showBookingModal && (
        <BookingModal
          trainer={trainer}
          onClose={() => setShowBookingModal(false)}
        />
      )}

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={closeLightbox}
        >
          <div className="relative max-w-7xl max-h-screen p-4 w-full h-full flex flex-col items-center justify-center">
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-2 hover:bg-black/70"
              aria-label="Close"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Image Title */}
            {lightboxTitle && (
              <div className="absolute top-4 left-4 bg-black/50 text-white px-4 py-2 rounded-lg backdrop-blur-sm">
                <p className="text-sm font-medium">{lightboxTitle}</p>
              </div>
            )}

            {/* Image Container with Animation */}
            <div 
              className="relative max-w-full max-h-full animate-in zoom-in duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={lightboxImage}
                alt={lightboxTitle}
                className="max-w-full max-h-[90vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/trainer-placeholder-logo-dark-t.png';
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainerProfilePage;